'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppAlertDialog } from '@/components/ui/alert-dialog';
import { logger } from '@/lib/logger';

/**
 * Imperative, promise-based alert dialog.
 *
 * The sibling of `confirmDialog()` in ./confirm — the drop-in replacement for
 * `window.alert()`. Browser-native dialogs are forbidden in this project: they
 * render the host's own chrome ("acme.dataver.co says …"), cannot be styled,
 * cannot carry rich content, and truncate badly on mobile.
 *
 *   await alertDialog({
 *     title: 'Some files were not added',
 *     message: 'skills.zip contains no readable skill.',
 *     type: 'error',
 *   });
 *
 * Use it where a message is long enough or important enough that a toast would
 * be the wrong surface — a rejected upload that has to explain what to fix.
 * Short, incidental feedback stays a toast.
 *
 * Mount <AlertDialogHost /> exactly once near the app root (layout.tsx).
 * Falls back to a toast-free no-op resolve if the host is not mounted, so a
 * missing host can never wedge an awaiting caller.
 */

export interface AlertOptions {
  title?: string;
  message: string;
  confirmText?: string;
  type?: 'info' | 'success' | 'warning' | 'error';
}

type Resolver = () => void;
let _open: ((opts: AlertOptions) => Promise<void>) | null = null;

export function alertDialog(opts: AlertOptions): Promise<void> {
  if (_open) return _open(opts);
  // No host yet (SSR, or called before mount). Never fall back to
  // window.alert — that is the exact chrome this helper exists to avoid.
  if (typeof console !== 'undefined') {
    logger.warn('[alertDialog] host not mounted:', opts.title, opts.message);
  }
  return Promise.resolve();
}

export function AlertDialogHost() {
  const [opts, setOpts] = useState<AlertOptions | null>(null);
  const resolverRef = useRef<Resolver | null>(null);

  useEffect(() => {
    _open = (o: AlertOptions) =>
      new Promise<void>((resolve) => {
        resolverRef.current = resolve;
        setOpts(o);
      });
    return () => { _open = null; };
  }, []);

  // Resolve once, outside the state updater (idempotent via the ref).
  const settle = useCallback(() => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpts(null);
    resolve?.();
  }, []);

  if (!opts) return null;

  return (
    <AppAlertDialog
      open
      onOpenChange={(next) => { if (!next) settle(); }}
      title={opts.title || 'Notice'}
      description={opts.message}
      type={opts.type || 'info'}
      confirmText={opts.confirmText || 'OK'}
      onConfirm={settle}
    />
  );
}
